import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { TrendingUp, TrendingDown, Shield, Heart, Eye, Zap, Sparkles, Loader2, Send, Radar as RadarIcon, Bookmark } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../../lib/track";
import { ObservatoryLoader } from "../common/ObservatoryLoader";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const IndustryObservatory = () => {
  const [dist, setDist] = useState(null);
  const [aicci, setAicci] = useState(null);
  const [appDist, setAppDist] = useState(null);
  const [devDist, setDevDist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [d, a, ad, dd] = await Promise.allSettled([
        axios.get(`${API}/industry/distribution`),
        axios.get(`${API}/industry/aicci`),
        axios.get(`${API}/industry/distribution?entity_type=application`),
        axios.get(`${API}/industry/distribution?entity_type=device`),
      ]);
      if (d.status === "fulfilled") setDist(d.value.data);
      if (a.status === "fulfilled") setAicci(a.value.data);
      if (ad.status === "fulfilled") setAppDist(ad.value.data);
      if (dd.status === "fulfilled") setDevDist(dd.value.data);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <ObservatoryLoader message="Mapping industry clusters" />;

  const topCare = aicci ? [...aicci].sort((a, b) => b.care_pct - a.care_pct).slice(0, 5) : [];
  const topExtraction = aicci ? [...aicci].sort((a, b) => b.extraction_pct - a.extraction_pct).slice(0, 5) : [];
  const aicciSorted = aicci ? [...aicci].sort((a, b) => b.aicci - a.aicci) : [];

  // Cross-galaxy comparison: top industries in apps vs devices
  const crossGalaxy = [];
  if (appDist && devDist) {
    const appMap = Object.fromEntries(appDist.distribution.map(d => [d.industry, d]));
    const devMap = Object.fromEntries(devDist.distribution.map(d => [d.industry, d]));
    const allIndustries = new Set([...Object.keys(appMap), ...Object.keys(devMap)]);
    allIndustries.forEach(ind => {
      crossGalaxy.push({
        industry: ind.replace(" & ", "\n& "),
        fullName: ind,
        apps: appMap[ind]?.count || 0,
        devices: devMap[ind]?.count || 0,
        appPct: appMap[ind]?.pct || 0,
        devPct: devMap[ind]?.pct || 0,
      });
    });
    crossGalaxy.sort((a, b) => (b.apps + b.devices) - (a.apps + a.devices));
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="industry-observatory">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl py-10 px-6 md:px-10 text-center"
        style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #0c1222 40%, #0a1628 100%)", border: "1px solid rgba(245,158,11,0.15)" }}>
        <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.25em] mb-3">Industry Intelligence</p>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">Which industries are building AI consciously?</h1>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          {dist?.total?.toLocaleString()} AI entities classified across 16 industry clusters.
          From healthcare to defense, every industry builds AI differently.
        </p>
      </div>

      {/* AICCI Leaderboard */}
      {aicciSorted.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.12)" }}>
          <div className="p-5 md:p-6">
            <div className="flex items-center gap-2 mb-5">
              <Shield className="w-4 h-4 text-violet-400" />
              <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.2em]">Industry Consciousness Leaderboard</p>
            </div>
            <div className="space-y-2">
              {aicciSorted.map((ind, i) => (
                <div key={ind.industry} className="flex items-center gap-3 group">
                  <span className="text-xs font-black text-slate-500 w-6 text-right">{i + 1}</span>
                  <span className="text-[10px] text-slate-400 w-40 truncate flex-shrink-0">{ind.industry}</span>
                  <div className="flex-1 h-3 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${ind.aicci}%`,
                      background: ind.aicci >= 70 ? "#10b981" : ind.aicci >= 40 ? "#f59e0b" : "#ef4444",
                    }} />
                  </div>
                  <span className="text-xs font-bold w-10 text-right" style={{
                    color: ind.aicci >= 70 ? "#10b981" : ind.aicci >= 40 ? "#f59e0b" : "#ef4444",
                  }}>{ind.aicci}</span>
                  <span className="text-[10px] text-slate-500 w-20 text-right hidden md:block">{ind.total.toLocaleString()} entities</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Care vs Top Extraction */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-emerald-500/[0.04] border border-emerald-500/15 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-emerald-400">Most Care-Oriented Industries</h3>
          </div>
          {topCare.map((ind, i) => (
            <div key={ind.industry} className="flex items-center justify-between py-2 border-b border-white/[0.03] last:border-0">
              <span className="text-xs text-slate-300">{ind.industry}</span>
              <span className="text-sm font-black text-emerald-400">{ind.care_pct}%</span>
            </div>
          ))}
        </div>
        <div className="bg-red-500/[0.04] border border-red-500/15 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-4 h-4 text-red-400" />
            <h3 className="text-sm font-bold text-red-400">Most Extraction-Heavy Industries</h3>
          </div>
          {topExtraction.map((ind, i) => (
            <div key={ind.industry} className="flex items-center justify-between py-2 border-b border-white/[0.03] last:border-0">
              <span className="text-xs text-slate-300">{ind.industry}</span>
              <span className="text-sm font-black text-red-400">{ind.extraction_pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cross-Galaxy Comparison */}
      {crossGalaxy.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-1">Apps vs Devices: Where Industries Diverge</h3>
          <p className="text-[10px] text-slate-400 mb-4">How industry presence differs between software and physical AI</p>
          <div style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={crossGalaxy.slice(0, 12)} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)"
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <YAxis type="category" dataKey="industry" tick={{ fill: "#94a3b8", fontSize: 9 }} width={100} stroke="rgba(255,255,255,0.08)"
                  tickFormatter={v => {
                    const SHORT = {"Entertainment & Gaming":"Entertainment","Developer Tools & Infrastructure":"Dev Tools","General Productivity":"Productivity","Creative & Media":"Creative","Education & Research":"Education","Finance & Insurance":"Finance","Healthcare & Life Sciences":"Healthcare","Retail & E-commerce":"Retail","Legal & Compliance":"Legal","Security & Defense":"Security","Social Impact & Nonprofits":"Social Impact","Social Impact & Nonprofit":"Social Impact","Agriculture & Environment":"Agriculture","Manufacturing & Robotics":"Manufacturing","Manufacturing & Industrial":"Manufacturing","Transportation & Logistics":"Transport","Communication & Telecom":"Telecom"};
                    return SHORT[v] || (v?.length > 14 ? v.substring(0, 12) + ".." : v);
                  }} />
                <Tooltip
                  contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v, name) => [v.toLocaleString(), name === "apps" ? "Software Apps" : "Physical AI Devices"]}
                />
                <Bar dataKey="apps" name="apps" fill="#34d399" fillOpacity={0.7} radius={[0, 4, 4, 0]} />
                <Bar dataKey="devices" name="devices" fill="#60a5fa" fillOpacity={0.7} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2 text-[10px]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-emerald-400/70" /> Software Apps</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-blue-400/70" /> Physical AI Devices</span>
          </div>
        </div>
      )}

      {/* Industry Distribution */}
      {dist && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-4">Full Industry Distribution</h3>
          <div className="fluid-grid-stats">
            {dist.distribution.map((d, i) => (
              <div key={d.industry} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 text-center hover:border-white/[0.12] transition-all">
                <p className="text-xl font-black" style={{ color: d.color }}>{d.count.toLocaleString()}</p>
                <p className="text-[10px] text-slate-400 mt-1 leading-tight">{d.industry}</p>
                <p className="text-[10px] text-slate-500">{d.pct}%</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Founder Diversity by Industry */}
      {aicci && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-1">Founder Diversity by Industry</h3>
          <p className="text-[10px] text-slate-400 mb-4">% of female-only + mixed founding teams</p>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...aicci].sort((a, b) => b.diversity_pct - a.diversity_pct).slice(0, 12)} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)" tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="industry" tick={{ fill: "#94a3b8", fontSize: 9 }} width={100} stroke="rgba(255,255,255,0.08)"
                  tickFormatter={v => {
                    const SHORT = {"Entertainment & Gaming":"Entertainment","Developer Tools & Infrastructure":"Dev Tools","General Productivity":"Productivity","Creative & Media":"Creative","Education & Research":"Education","Finance & Insurance":"Finance","Healthcare & Life Sciences":"Healthcare","Retail & E-commerce":"Retail","Legal & Compliance":"Legal","Security & Defense":"Security","Social Impact & Nonprofits":"Social Impact","Social Impact & Nonprofit":"Social Impact","Agriculture & Environment":"Agriculture","Manufacturing & Robotics":"Manufacturing","Manufacturing & Industrial":"Manufacturing","Transportation & Logistics":"Transport","Communication & Telecom":"Telecom"};
                    return SHORT[v] || (v?.length > 14 ? v.substring(0, 12) + ".." : v);
                  }} />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v) => [`${v}%`, "Diverse Teams"]} />
                <Bar dataKey="diversity_pct" radius={[0, 4, 4, 0]} animationDuration={800}>
                  {[...aicci].sort((a, b) => b.diversity_pct - a.diversity_pct).slice(0, 12).map((e) => (
                    <Cell key={`div-cell-${e.industry}`} fill="#ec4899" fillOpacity={0.5 + (e.diversity_pct / 100) * 0.5} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Industry Insight Lens */}
      <IndustryInsightLens industries={dist?.distribution || []} />

      {/* Footer */}
      <div className="text-center text-[10px] text-slate-500 py-4">
        Industry classification: rule-based keyword matching refined by GPT-4o-mini. {dist?.total?.toLocaleString()} entities across 16 clusters.
      </div>
    </div>
  );
};

/* ─── Industry Insight Lens ─── */
const PROGRESS_STAGES = [
  { at: 0, label: "Gathering industry data" },
  { at: 4, label: "Cross-referencing companies and policies" },
  { at: 9, label: "Analyzing patterns across products" },
  { at: 16, label: "Synthesizing insights" },
  { at: 24, label: "Drafting your brief" },
  { at: 34, label: "Almost there — finalizing" },
];

const IndustryInsightLens = ({ industries }) => {
  const [selected, setSelected] = useState("");
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(null);
  const [progress, setProgress] = useState({ stage: 0, elapsed: 0 });

  // Fetch suggestions whenever industry changes
  useEffect(() => {
    if (!selected) { setRecent(null); return; }
    let stale = false;
    axios.get(`${API}/industry/insights/recent`, { params: { industry: selected } })
      .then(r => { if (!stale) setRecent(r.data); })
      .catch(() => {});
    return () => { stale = true; };
  }, [selected]);

  // Drive staged progress while loading so 25-30s waits feel active
  useEffect(() => {
    if (!loading) { setProgress({ stage: 0, elapsed: 0 }); return; }
    const start = Date.now();
    const t = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      const stage = PROGRESS_STAGES.reduce((acc, s, i) => elapsed >= s.at ? i : acc, 0);
      setProgress({ stage, elapsed });
    }, 500);
    return () => clearInterval(t);
  }, [loading]);

  const generate = async (overrideQuery) => {
    if (!selected) return;
    const q = overrideQuery !== undefined ? overrideQuery : query;
    if (overrideQuery !== undefined) setQuery(overrideQuery);
    setLoading(true);
    setInsight(null);
    trackEvent("industry_insight", "industries", selected);
    try {
      const r = await axios.post(`${API}/industry/insights`,
        { industry: selected, query: q.trim() || null },
        { timeout: 90000 });
      setInsight(r.data);
    } catch { setInsight({ error: "Failed to generate. Please try again." }); }
    setLoading(false);
  };

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="industry-insight-lens"
      style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(245,158,11,0.15)" }}>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <RadarIcon className="w-4 h-4 text-amber-400" />
          <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.2em]">Industry Insight Lens</p>
        </div>
        <p className="text-sm text-slate-300 mb-4">Select an industry to generate deep AI-powered insights about its consciousness, patterns, and trajectory.</p>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="industry-select"
            className="flex-1 px-4 py-3 bg-black/40 border border-white/[0.08] rounded-xl text-sm text-white focus:outline-none focus:border-amber-500/30">
            <option value="">Choose an industry...</option>
            {industries.map(ind => (
              <option key={ind.industry} value={ind.industry}>{ind.industry} ({ind.count.toLocaleString()})</option>
            ))}
          </select>
          <div className="relative flex-1">
            <input value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") generate(); }}
              placeholder="Ask something specific (optional)..."
              className="w-full px-4 py-3 bg-black/40 border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/30 pr-24"
              data-testid="industry-insight-query" />
            <button onClick={() => generate()} disabled={!selected || loading} data-testid="industry-insight-btn"
              className="absolute top-1/2 -translate-y-1/2 right-2 flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:opacity-30 text-white text-xs font-bold rounded-lg transition-all">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {loading ? "Analyzing..." : "Generate"}
            </button>
          </div>
        </div>

        {/* Suggested questions — pre-warmed = instant, others ~25s */}
        {selected && recent?.suggestions?.length > 0 && !insight && !loading && (
          <div className="mb-4" data-testid="insight-suggestions">
            <div className="flex items-center gap-1.5 mb-2">
              <Bookmark className="w-3 h-3 text-amber-400/70" />
              <span className="text-[10px] font-bold text-amber-400/80 uppercase tracking-wider">
                Suggested · tap for instant answer
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recent.suggestions.map(s => (
                <button key={s.question} onClick={() => generate(s.question)}
                  data-testid={`suggestion-${s.warmed ? 'warm' : 'cold'}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                    s.warmed
                      ? "bg-emerald-500/[0.08] border-emerald-500/25 text-emerald-200 hover:bg-emerald-500/[0.15]"
                      : "bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-amber-500/[0.10] hover:border-amber-500/25"
                  }`}>
                  {s.warmed && <span className="mr-1">⚡</span>}
                  {s.question}
                </button>
              ))}
            </div>
            {recent.recent?.length > 0 && (
              <p className="text-[10px] text-slate-500 mt-2 italic">
                {recent.recent.length} previously-asked question{recent.recent.length === 1 ? "" : "s"} cached for {selected}
              </p>
            )}
          </div>
        )}

        {/* Staged loading indicator */}
        {loading && (
          <div className="mb-4 bg-amber-500/[0.04] border border-amber-500/[0.15] rounded-xl p-4" data-testid="insight-progress">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-4 h-4 text-amber-400 animate-spin flex-shrink-0" />
              <p className="text-sm text-amber-200 font-medium">{PROGRESS_STAGES[progress.stage].label}…</p>
              <span className="text-[10px] text-amber-400/70 ml-auto font-mono tabular-nums">{progress.elapsed.toFixed(1)}s</span>
            </div>
            {/* Stepped bar */}
            <div className="flex gap-1">
              {PROGRESS_STAGES.map((s, i) => (
                <div key={s.label} className="flex-1 h-1 rounded-full overflow-hidden bg-white/[0.05]">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500"
                       style={{ width: i < progress.stage ? "100%" : i === progress.stage ? "60%" : "0%" }} />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Cross-referencing 100K+ entities · this typically takes 15–25 seconds the first time, then is instant from cache.</p>
          </div>
        )}

        {/* Results */}
        {insight && !insight.error && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-amber-500/[0.06] border border-amber-500/15 rounded-xl p-5">
              <h3 className="text-base font-bold text-white mb-2">{insight.headline}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{insight.analysis}</p>
            </div>
            {insight.key_findings?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {insight.key_findings.map((f, i) => (
                  <div key={`finding-${f.stat || i}-${i}`} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-lg font-black text-amber-400 mb-1">{f.stat}</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{f.insight}</p>
                  </div>
                ))}
              </div>
            )}
            {insight.recommendation && (
              <div className="bg-violet-500/[0.06] border border-violet-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider mb-1">What This Means</p>
                <p className="text-sm text-slate-300 leading-relaxed">{insight.recommendation}</p>
              </div>
            )}
            {insight.notable_innovations?.length > 0 && (
              <div className="bg-emerald-500/[0.04] border border-emerald-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-3">Notable Innovations</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {insight.notable_innovations.map((inn, i) => {
                    const isObj = typeof inn === "object";
                    const name = isObj ? inn.name : inn;
                    const orientColor = { care: "#10b981", utility: "#3b82f6", creative: "#8b5cf6", extraction: "#ef4444" };
                    return (
                      <div key={`innovation-${name}-${i}`} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 hover:border-white/[0.12] transition-all">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-xs font-bold text-white">{name}</h4>
                          {isObj && inn.orientation && (
                            <span className="text-[8px] px-2 py-0.5 rounded-full flex-shrink-0 font-bold uppercase" style={{ background: `${orientColor[inn.orientation] || "#475569"}15`, color: orientColor[inn.orientation] || "#94a3b8" }}>{inn.orientation}</span>
                          )}
                        </div>
                        {isObj && inn.tagline && <p className="text-[10px] text-slate-400 leading-relaxed mb-1">{inn.tagline}</p>}
                        {isObj && inn.why_notable && <p className="text-[10px] text-emerald-400/80 leading-relaxed italic">{inn.why_notable}</p>}
                        {isObj && inn.country && <p className="text-[10px] text-slate-500 mt-1">{inn.country}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {insight.trends?.length > 0 && (
              <div className="bg-blue-500/[0.04] border border-blue-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-3">Key Trends</p>
                <div className="space-y-3">
                  {insight.trends.map((trend, i) => (
                    <div key={`trend-${typeof trend === "string" ? trend.slice(0, 40) : i}-${i}`} className="flex items-start gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-300 leading-relaxed">{trend}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {insight.gaps && (
              <div className="bg-red-500/[0.04] border border-red-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1">Blind Spots & Gaps</p>
                <p className="text-xs text-slate-300 leading-relaxed">{insight.gaps}</p>
              </div>
            )}
            {insight.impact_areas?.length > 0 && (
              <div className="bg-cyan-500/[0.04] border border-cyan-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider mb-3">Impact Areas</p>
                <div className="space-y-3">
                  {insight.impact_areas.map((area, i) => {
                    const isObj = typeof area === "object";
                    const maturityColor = { emerging: "#f59e0b", growing: "#3b82f6", mature: "#10b981" };
                    const areaName = isObj ? area.area : area;
                    return (
                      <div key={`impact-${areaName}-${i}`} className="flex items-start gap-3 pb-3 border-b border-white/[0.04] last:border-0 last:pb-0">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-xs font-bold text-white">{isObj ? area.area : area}</h4>
                            {isObj && area.maturity && (
                              <span className="text-[8px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ background: `${maturityColor[area.maturity] || "#475569"}15`, color: maturityColor[area.maturity] || "#94a3b8" }}>{area.maturity}</span>
                            )}
                          </div>
                          {isObj && area.description && <p className="text-[10px] text-slate-400 leading-relaxed">{area.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        {insight?.error && (
          <p className="text-sm text-red-400 mt-3">{insight.error}</p>
        )}
      </div>
    </div>
  );
};

export default IndustryObservatory;
